import { Flex, Stack } from '@mantine/core'
import { IconTrash } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { deleteBlock, getListBlock } from 'src/apis/blockAPI'
import CustomClearInput from 'src/components/CustomComponents/CustomClearInput'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'

import TableComponent from 'src/components/TableComponent'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { color, formatDateTime, size } from 'src/variable/constant'

const LinkCustomTooltip = ({ label, maxWidth, onClick }) => (
	<CustomTooltip
		label={label}
		sx={{
			maxWidth: 400,
			whiteSpace: 'normal',
			padding: 10,
			wordBreak: 'break-word',
			position: 'fixed',
		}}
	>
		<div
			onClick={onClick}
			style={{
				maxWidth,
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
				cursor: onClick ? 'pointer' : 'default',
				color: onClick ? color.Blue : 'inherit',
				textDecoration: onClick ? 'underline' : 'none',
			}}
		>
			{label}
		</div>
	</CustomTooltip>
)

const typeOptions = [
	{ label: 'All', value: 'ALL' },
	{ label: 'User', value: 'USER' },
	{ label: 'Post', value: 'POST' },
]

const searchFields = [
	{ label: 'Table ID', value: 'table_id' },
	{ label: 'Blocker ID', value: 'blocker_id' },
	{ label: 'Blocker name', value: 'blocker_name' },
	{ label: 'Blocker phone', value: 'blocker_phone' },
	{ label: 'Blocked ID', value: 'blocked_id' },
	{ label: 'Blocked name/title', value: 'blocked_name' },
	{ label: 'Blocked phone', value: 'blocked_phone' },
]

const getBlockedDisplay = (item: any) => {
	if (item?.table_type === 'USER') {
		return {
			id: item?.blocked_user?.id || item?.table_id || '-',
			name: item?.blocked_user?.name || '-',
			phone: item?.blocked_user?.phone || '-',
		}
	}

	if (item?.table_type === 'POST') {
		return {
			id: item?.blocked_post?.id || item?.table_id || '-',
			name: item?.blocked_post?.title || '-',
			phone: '-',
		}
	}

	return {
		id: item?.table_id || '-',
		name: '-',
		phone: '-',
	}
}

function BlockPage() {
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const navigate = useNavigate()
	const didMountFilter = useRef(false)
	const [data, setData] = useState<any[]>([])
	const [total, setTotal] = useState<number>(0)
	const [loading, setLoading] = useState<boolean>(false)
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})
	const [filter, setFilter] = useState({
		table_type: 'ALL',
		field: 'table_id',
		value: '',
	})

	const columns = useMemo<MRT_ColumnDef[]>(
		() => [
			{
				accessorKey: 'no',
				header: 'No',
				size: size.zero,
			},
			{
				accessorKey: 'blocker_display',
				header: 'Blocker',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell, row }) => {
					const original = row.original as any
					return (
						<LinkCustomTooltip
							label={cell.getValue<string>() || '-'}
							maxWidth={size.text}
							onClick={
								original?.blocker_id && original?.blocker_id !== '-'
									? () => navigate(`/users/${original.blocker_id}`)
									: undefined
							}
						/>
					)
				},
			},
			{
				accessorKey: 'table_type',
				header: 'Type',
				size: size.zero,
			},
			{
				accessorKey: 'blocked_display',
				header: 'Blocked',
				size: size.zero,
				maxSize: size.medium,
				Cell: ({ cell, row }) => {
					const original = row.original as any
					return (
						<LinkCustomTooltip
							label={cell.getValue<string>() || '-'}
							maxWidth={size.medium}
							onClick={
								original?.table_type === 'USER' &&
								original?.blocked_id &&
								original?.blocked_id !== '-'
									? () => navigate(`/users/${original.blocked_id}`)
									: undefined
							}
						/>
					)
				},
			},
			{
				accessorKey: 'topic',
				header: 'Topic',
				size: size.zero,
				maxSize: size.text,
				Cell: ({ cell }) => (
					<CustomTooltip label={cell.getValue<string>() || '-'} maxWidth={size.text} />
				),
			},
			{
				accessorKey: 'content',
				header: 'Content',
				size: size.zero,
				maxSize: size.medium,
				Cell: ({ cell }) => (
					<CustomTooltip label={cell.getValue<string>() || '-'} maxWidth={size.medium} />
				),
			},
			{
				accessorKey: 'created_at',
				header: 'Created at',
				size: size.zero,
				maxSize: size.text,
			},
		],
		[navigate],
	)

	const handleGetData = useCallback(async () => {
		setLoading(true)
		try {
			const res = await getListBlock({
				page: pagination.pageIndex,
				limit: pagination.pageSize,
				table_type: filter.table_type,
				field: filter.field,
				value: filter.value,
			})
			const rows = res?.data?.results?.objects?.rows || []
			const mappedRows = rows.map((item, index) => {
				const blockedDisplay = getBlockedDisplay(item)
				return {
					...item,
					no: index + 1 + pagination.pageSize * (pagination.pageIndex - 1),
					blocker_id: item?.blocker?.id || item?.user_id || '-',
					blocker_name: item?.blocker?.name || '-',
					blocker_phone: item?.blocker?.phone || '-',
					blocker_display: [item?.blocker?.name, item?.blocker?.phone]
						.filter(Boolean)
						.join(' - ') || '-',
					blocked_id: blockedDisplay.id,
					blocked_name: blockedDisplay.name,
					blocked_phone: blockedDisplay.phone,
					blocked_display: [blockedDisplay.name, blockedDisplay.phone]
						.filter((value) => value && value !== '-')
						.join(' - ') || '-',
					created_at: item?.created_at
						? dayjs(item.created_at).format(formatDateTime)
						: '-',
				}
			})

			setData(mappedRows)
			setTotal(res?.data?.results?.objects?.count || 0)
		} catch (error) {
			openError(error)
		} finally {
			setTimeout(() => setLoading(false), 300)
		}
	}, [filter, openError, pagination])

	const handleChangePage = useCallback((value: number) => {
		setPagination((pre) => ({
			...pre,
			pageIndex: value,
		}))
	}, [])

	const handleSetFilter = useCallback((key: string, value: string) => {
		setFilter((pre) => ({ ...pre, [key]: value }))
	}, [])

	const handleDelete = useCallback(
		async (row: any) => {
			openLoadingContext()
			try {
				const res = await deleteBlock(row?.original?.id)
				if (res?.data?.code === 200) {
					await handleGetData()
					openModal({
						type: 'success',
						label: 'Block has been removed successfully',
					})
				}
			} catch (error) {
				openError(error)
			} finally {
				closeLoadingContext()
			}
		},
		[closeLoadingContext, handleGetData, openError, openLoadingContext, openModal],
	)

	const handleConfirmDelete = useCallback(
		(row: any) => {
			openModal({
				type: 'confirm',
				label: <div>Do you want to remove this block?</div>,
				handleConfirm: () => handleDelete(row),
			})
		},
		[handleDelete, openModal],
	)

	const renderCustomActions = useCallback(
		(row) => (
			<Stack sx={{ order: 30 }}>
				<CustomTooltip label="Remove block">
					<IconTrash
						style={{ cursor: 'pointer', color: 'red' }}
						onClick={() => handleConfirmDelete(row)}
					/>
				</CustomTooltip>
			</Stack>
		),
		[handleConfirmDelete],
	)

	useEffect(() => {
		handleGetData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(pagination)])

	useEffect(() => {
		if (!didMountFilter.current) {
			didMountFilter.current = true
			return
		}
		const timer = setTimeout(() => {
			if (pagination.pageIndex === 1) {
				handleGetData()
			} else {
				handleChangePage(1)
			}
		}, 500)

		return () => clearTimeout(timer)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(filter)])

	return (
		<Stack sx={{ width: '100%' }}>
			<Flex
				gap={8}
				justify="space-between"
				sx={{ flexWrap: 'wrap' }}
				align="center"
			>
				<Flex gap={8} sx={{ flexWrap: 'wrap' }}>
					<CustomSelect
						data={typeOptions}
						value={filter.table_type}
						placeholder="Type"
						onChange={(value) => handleSetFilter('table_type', value || 'ALL')}
					/>
					<CustomSelect
						data={searchFields}
						value={filter.field}
						placeholder="Search field"
						onChange={(value) => handleSetFilter('field', value || 'table_id')}
					/>
					<CustomClearInput
						value={filter.value}
						onChange={(value) => handleSetFilter('value', value)}
						placeholder="Search"
					/>
				</Flex>
				<Flex
					gap={8}
					align="center"
					justify="flex-end"
					sx={{ fontSize: 20, fontWeight: 600, color: color.CmsPrimary }}
				>
					<div>Total: {total || ''}</div>
				</Flex>
			</Flex>
			<TableComponent
				columns={columns}
				total={total}
				data={data}
				pagination={pagination}
				loading={loading}
				handleChangePage={handleChangePage}
				syncQueryUrl
				// searchQueryValue={filter.value}
				// onSearchQueryInit={handleSearchQueryInit}
				// filterQueryValues={{
				// 	table_type: filter.table_type !== 'ALL' ? filter.table_type : undefined,
				// 	field: filter.field !== 'table_id' ? filter.field : undefined,
				// }}
				// onFilterQueryInit={handleFilterQueryInit}
				_renderCustomActions={renderCustomActions}
			/>
		</Stack>
	)
}

export default React.memo(BlockPage)
